<template>
  <ul class="grid md:grid-cols-2 gap-3">
    <li
      v-for="detail in detailValues"
      :key="detail.name"
      class="flex justify-between"
    >
      <strong>{{ detail.name }}</strong>
      <span class="text-right whitespace-pre-wrap">{{ detail.value }}</span>
    </li>
  </ul>
</template>
<script>

import { DateTime } from 'luxon';
import lodash from 'lodash';

export default {
  props: {
    details: {
      type: Array,
      required: true,
    },
    property: {
      type: Object,
      required: true,
    },
  },
  computed: {
    detailValues() {
      return this.details.map((detail) => {
        const processedValue = {
          ...detail,
          value: this.getDetailValue(detail),
        };

        if (Object.hasOwn(processedValue, 'name')) {
          const nameAsDetail = this.getDetailValue(detail.name);

          if (nameAsDetail) {
            processedValue.name = nameAsDetail;
          }
        }

        if (!lodash.isString(processedValue.name)) {
          return false;
        }

        return processedValue;
      }).filter((d) => d.value);
    },
  },
  methods: {
    getDetailValue(detail, object = this.property) {
      if (detail === null) {
        return detail;
      }

      if (Object.hasOwn(detail, 'merge')) {
        const items = detail.merge.details
          .map((d) => this.getDetailValue(d, object)).filter((i) => i);
        return items.join(detail.merge.separator);
      }

      let value = lodash.get(object, detail.valuePath);

      if (typeof value === 'undefined') {
        return null;
      }

      if (Object.hasOwn(detail, 'arrayFormat')) {
        if (Object.hasOwn(detail, 'changesOn')) {
          let lastDatum = this.getDetailValue(detail.changesOn, value[0]);
          const changes = [value[0]];

          value.forEach((v) => {
            const currentDatum = this.getDetailValue(detail.changesOn, v);

            if (lastDatum !== currentDatum) {
              changes.push(v);
            }

            lastDatum = currentDatum;
          });

          value = changes;
        }
        value = value
          .slice(0, detail.limit)
          .map((v) => this.getDetailValue(detail.arrayFormat, v))
          .join(detail.separator);
      }

      if (detail.dollarFormat) {
        let options = {
          style: 'currency',
          currency: 'USD',
        };
        if (Object.hasOwn(detail, 'decimalPlaces')) {
          options = {
            ...options,
            minimumFractionDigits: detail.dollarFormat.decimalPlaces,
            maximumFractionDigits: detail.dollarFormat.decimalPlaces,
          };
        }
        value = parseFloat(value);

        if (!value) {
          return null;
        }

        value = value.toLocaleString('en-US', options);
      }

      if (detail.booleanToYesNo) {
        value = value ? 'Yes' : 'No';
      }

      if (detail.dateFormat) {
        if (value) {
          value = DateTime.fromISO(value).toFormat(detail.dateFormat);
        }
      }

      if (Object.hasOwn(detail, 'decimalPlaces')) {
        if (value) {
          value = parseFloat(value);

          if (!value) {
            return null;
          }

          value = value.toFixed(detail.decimalPlaces);
        }
      }

      if (detail.separateCommas) {
        if (typeof value === 'string') {
          value = value
            .split(',')
            .map((s) => s.trim())
            .join(', ');
        }
      }

      if (detail.thousandSeparator) {
        if (value) {
          value = parseInt(value, 10).toLocaleString();
        }
      }

      if (detail.prefix) {
        if (value) {
          value = detail.prefix + value;
        }
      }

      if (detail.suffix) {
        if (value) {
          value += detail.suffix;
        }
      }

      if (detail.substitutions) {
        detail.substitutions.forEach((substitution) => {
          if (value) {
            value = value.replace(substitution.find, substitution.replace);
          }
        });
      }

      return value;
    },
  },
};
</script>
